import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { PaperButtonComponent, PaperIconComponent, PaperTagComponent } from '@ricoh-paper/angular';
import { TranslocoPipe } from '@jsverse/transloco';

import type { BuildingAlert } from '../../../../core/services/building-alerts.service';
import { PopoverBandService } from '../../../../core/overlays/popover-band.service';
import { anchorFrom, type PopoverAnchor } from '../../../../core/overlays/anchor.util';
import { PaperAlertsBannerComponent } from '../../../../shared/paper-app/paper-alerts-banner';
import { beaconSegments, beaconTone, blockingCount } from './alert-beacon-logic';

let nextBeaconId = 0;

/** The beacon's key in the popover band — the `data-popover` value AND the
 *  key its close callback is filed under. One string, so they cannot drift. */
export const ALERT_BEACON_POPOVER_KEY = 'shell.alert-beacon';

/** Why a collapse happened. `dismiss` is the band's click-away OR Escape:
 *  the band deliberately does not distinguish them (ONE capture-phase
 *  listener for nineteen popovers), and inventing the split here would be
 *  reporting a fact this component cannot observe. */
export type AlertBeaconToggle = {
  readonly expanded: boolean;
  readonly reason: 'toggle' | 'dismiss';
};

/**
 * im-alert-beacon — the compact statement of tenant building alerts over a
 * full-bleed canvas, with the detail on demand.
 *
 * WHY IT EXISTS. `<paper-alerts-banner>` is the right component in a panel
 * and the wrong one over a map. Even collapsed its map form was a 680px
 * plate pinned under the shell bar, covering the search field; expanded it
 * became a three-card list over roughly half the floorplan at 1440 and over
 * nearly all of it at 390. The canvas is the page. A band that grows into it
 * is the plate §3.1 removed from the bar, reintroduced one row lower.
 *
 * WHAT IT TAKES FROM MANAGE. The estate map (`estate-map-page.component`)
 * solved this: a glass pill of counts floating in the corner, zero-count
 * buckets dropped, the detail carried elsewhere. The counting rule and the
 * drop-the-zeroes rule are lifted wholesale (`alert-beacon-logic.ts`).
 *
 * WHAT IT DOES DIFFERENTLY, deliberately:
 *   - Manage's counts are FILTER CHIPS over a plan it can dim and a side
 *     list it can narrow. The employee map has neither, so these are a
 *     readout: one control, one popover, no chip that looks pressable and
 *     does the same thing as its neighbour.
 *   - Manage's detail is a `paper-side-panel` anchored to its sheet. Here
 *     the detail is a popover anchored to the pill, because the alerts are
 *     an announcement about the building rather than a workspace to sit in.
 *
 * HOW A BLOCKING CLOSURE STAYS UNMISSABLE — compact is not the same as
 * quiet, and four separate things carry that:
 *   1. TONE. `beaconTone` takes the loudest tone the contents justify, never
 *      the average, so one closure among five notices still paints the pill
 *      in `--error-*`. Over a grey floorplan it is the only coloured chrome.
 *   2. HATCH. The plaque behind the glyph is hatched whenever something is
 *      closed. That is the estate-wide language for out-of-service — Manage
 *      hatches closed polygons, Louis's Schedule rig hatches closed lanes —
 *      and it survives greyscale, high-contrast and a mono printout, which
 *      colour alone does not.
 *   3. WORDS. The pill states "N closed", not a bare badge count, and adds
 *      an explicit "Bookings blocked" tag where there is room for it.
 *   4. ANNOUNCEMENT + ATTENTION. A `role="status"` line states the summary
 *      to assistive tech, and the pill plays a one-shot attention pulse when
 *      a blocking alert is present (suppressed under `prefers-reduced-
 *      motion`, where the tone and the hatch carry it alone).
 *
 * It is also NOT dismissible from the compact state: the X lives on each row
 * INSIDE the popover, so a closure cannot be waved away without opening it.
 *
 * AND THE REAL GUARANTEE IS NOT VISUAL. Booking enforcement does not live
 * here. A blocked space is rejected by the booking validator, excluded from
 * quick-book and auto-book, and dropped from schedule suggestions
 * (`BuildingAlertsService` header, `blockBookings`) — dismissing this
 * announcement changes none of that.
 */
@Component({
  selector: 'im-alert-beacon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    PaperAlertsBannerComponent,
    PaperButtonComponent,
    PaperIconComponent,
    PaperTagComponent,
    TranslocoPipe,
  ],
  host: { class: 'ph-no-capture' },
  templateUrl: './alert-beacon.component.html',
  styleUrl: './alert-beacon.component.scss',
})
export class AlertBeaconComponent {
  private readonly band = inject(PopoverBandService);
  private readonly destroyRef = inject(DestroyRef);

  /** Active alerts to summarise. Empty array renders nothing. */
  readonly alerts = input.required<BuildingAlert[]>();

  readonly expandedChange = output<AlertBeaconToggle>();
  readonly dismissAlert = output<BuildingAlert>();
  readonly ctaClick = output<{
    alert: BuildingAlert;
    mode: 'internal' | 'external' | 'emit';
  }>();

  protected readonly panelId = `alert-beacon-panel-${++nextBeaconId}`;
  protected readonly popoverKey = ALERT_BEACON_POPOVER_KEY;

  /**
   * The pill, read through the overlay ORIGIN rather than a second template
   * reference. One reference: `anchorFrom` measures the same element CDK
   * anchors to, so the popover cannot be measured off one box and portalled
   * against another. A template ref on a component element resolves to the
   * component instance, not its host, which is the other trap here.
   */
  private readonly origin = viewChild(CdkOverlayOrigin);

  protected readonly open = signal(false);
  /** Measured from the trigger at open time — see `anchorFrom`. The popover
   *  is `position: fixed` INSIDE the CDK pane, which is why it needs its own
   *  box; the pane's job here is to escape the containing block, not to
   *  place anything. */
  protected readonly box = signal<PopoverAnchor | null>(null);

  protected readonly segments = computed(() => beaconSegments(this.alerts()));
  protected readonly tone = computed(() => beaconTone(this.alerts()));
  protected readonly blocking = computed(() => blockingCount(this.alerts()));

  /**
   * Face glyph. `alert-circle` when anything is closed or urgent, matching
   * `iconForSeverity('urgent')` in the alerts banner — one alert must not
   * wear two different faces in one session, and the popover this opens is
   * that banner.
   */
  protected readonly glyph = computed(() =>
    this.tone() === 'error'
      ? 'alert-circle'
      : this.tone() === 'warning'
        ? 'alert-triangle'
        : 'alert-info',
  );

  constructor() {
    // A registration that outlives its component keeps answering "close me"
    // for a popover that no longer exists. `close()` on an unknown key is a
    // no-op, so this is safe unconditionally.
    this.destroyRef.onDestroy(() => this.band.close(ALERT_BEACON_POPOVER_KEY));
  }

  protected toggle(): void {
    if (this.open()) {
      this.closeFromToggle();
      return;
    }
    // Measure, THEN open — `anchorFrom` reads the trigger's rect while the
    // popover is still absent, and the band's mutual exclusion runs inside
    // `open()`, so whatever else was up is shut before this appears.
    const trigger = this.origin()?.elementRef.nativeElement ?? null;
    this.box.set(anchorFrom(trigger, { size: 'lg', align: 'centre' }));
    this.open.set(true);
    this.expandedChange.emit({ expanded: true, reason: 'toggle' });
    this.band.open(ALERT_BEACON_POPOVER_KEY, {
      close: () => this.dismissFromBand(),
      trigger,
    });
  }

  /** Close from the app side (the pill, a CDK detach). Goes through the band
   *  so the registration is dropped and focus returns to the trigger. */
  protected closeFromToggle(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.expandedChange.emit({ expanded: false, reason: 'toggle' });
    this.band.close(ALERT_BEACON_POPOVER_KEY);
  }

  /** Local state only — this is what the BAND calls (click-away / Escape /
   *  mutual exclusion). It must never call back into the band. */
  private dismissFromBand(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.expandedChange.emit({ expanded: false, reason: 'dismiss' });
  }
}
